(() => {
  if (customElements.get('optimal-select')) return;
  let sequence = 0;
  let opened;
  class OptimalSelect extends HTMLElement {
    connectedCallback() {
      const select = this.querySelector('select');
      if (!select || this.ready) return;
      this.ready = true;
      const controller = new AbortController();
      const on = (node, event, fn) => node.addEventListener(event, fn, { signal: controller.signal });
      const id = `optimal-select-${++sequence}`;
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'optimal-select__trigger';
      trigger.setAttribute('role', 'combobox');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', id);
      const menu = document.createElement('div');
      menu.id = id;
      menu.className = 'optimal-select__menu';
      menu.setAttribute('role', 'listbox');
      menu.hidden = true;
      const originalTab = select.getAttribute('tabindex');
      const originalAria = select.getAttribute('aria-hidden');
      select.dataset.styledSelect = 'true';
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      this.append(trigger);
      document.body.append(menu);
      let active = select.selectedIndex;
      let search = '', typedAt = 0;
      const label = [...select.labels].map(item => item.textContent.trim()).join(' ') || select.getAttribute('aria-label') || 'Choose an option';
      menu.setAttribute('aria-label', label);
      const enabled = index => select.options[index] && !select.options[index].disabled && !select.options[index].parentElement.disabled;
      const highlight = () => {
        [...menu.children].forEach((item, index) => item.classList.toggle('is-focused', index === active));
        const item = menu.children[active];
        if (item) { trigger.setAttribute('aria-activedescendant', item.id); item.scrollIntoView({ block: 'nearest' }); }
      };
      const position = () => {
        const box = trigger.getBoundingClientRect();
        const spaceBelow = innerHeight - box.bottom - 12;
        const above = spaceBelow < 180 && box.top > spaceBelow;
        menu.style.width = `${Math.min(Math.max(box.width, 240), innerWidth - 24)}px`;
        menu.style.maxHeight = `${Math.max(80, Math.min(320, above ? box.top - 12 : spaceBelow))}px`;
        menu.style.left = `${Math.max(12, Math.min(box.left, innerWidth - menu.offsetWidth - 12))}px`;
        menu.style.top = above ? 'auto' : `${box.bottom + 6}px`;
        menu.style.bottom = above ? `${innerHeight - box.top + 6}px` : 'auto';
      };
      const close = () => {
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.removeAttribute('aria-activedescendant');
        if (opened === this) opened = null;
      };
      this.close = close;
      const sync = () => {
        trigger.textContent = select.selectedOptions[0]?.textContent || label;
        trigger.setAttribute('aria-label', `${label}: ${trigger.textContent}`);
        trigger.disabled = select.disabled;
        this.hidden = select.hidden;
        if (select.disabled || select.hidden) close();
        menu.replaceChildren(...[...select.options].map((option, index) => {
          const item = document.createElement('div');
          item.id = `${id}-${index}`;
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', String(option.selected));
          item.setAttribute('aria-disabled', String(!enabled(index)));
          item.dataset.index = index;
          item.textContent = option.textContent;
          return item;
        }));
      };
      const open = () => {
        if (select.disabled) return;
        opened?.close();
        opened = this;
        active = select.selectedIndex;
        sync();
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        position();
        highlight();
      };
      const commit = index => {
        if (!enabled(index)) return;
        const changed = select.selectedIndex !== index;
        select.selectedIndex = index;
        close();
        sync();
        trigger.focus({ preventScroll: true });
        if (changed) {
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      on(trigger, 'click', () => menu.hidden ? open() : close());
      on(menu, 'pointerdown', event => event.preventDefault());
      on(menu, 'click', event => {
        const option = event.target.closest('[data-index]');
        if (option) commit(Number(option.dataset.index));
      });
      on(trigger, 'keydown', event => {
        if (event.key === 'Tab' || event.key === 'Escape') { close(); return; }
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (menu.hidden) open();
          const direction = event.key === 'ArrowUp' || event.key === 'End' ? -1 : 1;
          let index = event.key === 'Home' ? 0 : event.key === 'End' ? select.length - 1 : active + direction;
          while (index >= 0 && index < select.length && !enabled(index)) index += direction;
          if (enabled(index)) active = index;
          highlight();
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (menu.hidden) open(); else commit(active);
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          search = Date.now() - typedAt > 700 ? event.key : search + event.key;
          typedAt = Date.now();
          if (menu.hidden) open();
          const index = [...select.options].findIndex((option, index) => enabled(index) && option.textContent.trim().toLocaleLowerCase().startsWith(search.toLocaleLowerCase()));
          if (index >= 0) { active = index; highlight(); }
        }
      });
      on(document, 'pointerdown', event => { if (!this.contains(event.target) && !menu.contains(event.target)) close(); });
      on(document, 'focusin', event => { if (!this.contains(event.target)) close(); });
      on(window, 'resize', close);
      on(window, 'scroll', close);
      on(select, 'change', sync);
      on(select, 'focus', () => trigger.focus());
      if (select.form) on(select.form, 'reset', () => queueMicrotask(sync));
      const observer = new MutationObserver(sync);
      observer.observe(select, { attributes: true, childList: true, subtree: true, attributeFilter: ['disabled', 'selected', 'hidden', 'label'] });
      this.dispose = () => {
        close(); controller.abort(); observer.disconnect(); menu.remove(); trigger.remove();
        delete select.dataset.styledSelect;
        originalTab === null ? select.removeAttribute('tabindex') : select.setAttribute('tabindex', originalTab);
        originalAria === null ? select.removeAttribute('aria-hidden') : select.setAttribute('aria-hidden', originalAria);
        this.ready = false;
      };
      sync();
    }
    disconnectedCallback() { this.dispose?.(); }
  }
  customElements.define('optimal-select', OptimalSelect);
  const initialize = (root = document) => {
    root.querySelectorAll('select:not([multiple])').forEach(select => {
      if (select.closest('optimal-select') || select.hidden || select.size > 1 || getComputedStyle(select).display === 'none') return;
      const parent = select.parentNode, next = select.nextSibling;
      const wrapper = document.createElement('optimal-select');
      wrapper.append(select);
      parent.insertBefore(wrapper, next);
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', event => requestAnimationFrame(() => initialize(event.target)));
})();
